/**
 * Reelia API Service Layer
 * All backend calls go through this module
 */

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export const getAuthToken = () => localStorage.getItem('reelia_token');

/** Global 401 handling: a dead token must log the user out, not leave empty pages. */
function handleUnauthorized() {
  localStorage.removeItem('reelia_token');
  localStorage.removeItem('reelia_user');
  localStorage.removeItem('reelia_show_style_match');
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
}

class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

async function request(method, path, body = null, isFormData = false) {
  const headers = {};
  if (!isFormData && body) {
    headers['Content-Type'] = 'application/json';
  }
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const opts = { method, headers };
  if (body) {
    opts.body = isFormData ? body : JSON.stringify(body);
  }

  const res = await fetch(`${BASE_URL}/api${path}`, opts);
  if (res.status === 401) {
    handleUnauthorized();
    throw new ApiError('Sesión caducada', 401);
  }
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      if (!res.bodyUsed) {
        const err = await res.json();
        msg = err.detail || err.message || msg;
      }
    } catch {}
    throw new ApiError(msg, res.status);
  }
  // 204 No Content
  if (res.status === 204) return null;
  return res.json();
}

const get = (path) => request('GET', path);
const post = (path, body) => request('POST', path, body);
const patch = (path, body) => request('PATCH', path, body);
const del = (path) => request('DELETE', path);

// ─── AUTH ──────────────────────────────────────────────────────────────────────
export const authApi = {
  register: (data) => post('/auth/register', data),
  login: (data) => post('/auth/login', data),
  me: () => get('/auth/me'),
  getUser: (userId) => get(`/users/${userId}`),
  updateUser: (userId, data) => patch(`/users/${userId}`, data),
};

// ─── CREATORS ──────────────────────────────────────────────────────────────────
export const creatorsApi = {
  list: (agencyId) => get(`/creators${agencyId ? `?agency_id=${agencyId}` : ''}`),
  create: (data) => post('/creators', data),
  get: (id) => get(`/creators/${id}`),
  delete: (id) => del(`/creators/${id}`),
};

// ─── BRANDS ────────────────────────────────────────────────────────────────────
export const brandsApi = {
  list: (creatorId) => get(`/brands${creatorId ? `?creator_id=${creatorId}` : ''}`),
  create: (data) => post('/brands', data),
  get: (id) => get(`/brands/${id}`),
  update: (id, data) => patch(`/brands/${id}`, data),
  delete: (id) => del(`/brands/${id}`),
};

// ─── SOCIAL ACCOUNTS ───────────────────────────────────────────────────────────
export const socialApi = {
  list: (brandId) => get(`/social-accounts${brandId ? `?brand_id=${brandId}` : ''}`),
  connect: (data) => post('/social-accounts', data),
  toggle: (id) => patch(`/social-accounts/${id}/toggle`, {}),
  delete: (id) => del(`/social-accounts/${id}`),
};

// ─── DRIVE ─────────────────────────────────────────────────────────────────────
export const driveApi = {
  listFiles: ({ brandId, folder, search, skip = 0, limit = 0 } = {}) => {
    const params = new URLSearchParams();
    if (brandId) params.set('brand_id', brandId);
    if (folder) params.set('folder', folder);
    if (search) params.set('search', search);
    if (skip > 0) params.set('skip', skip);
    if (limit > 0) params.set('limit', limit);
    const q = params.toString();
    return get(`/drive/files${q ? `?${q}` : ''}`);
  },

  uploadFile: async (file, { brandId, creatorId, folder = 'raw', tags = '' }, onProgress, onXhrCreated) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('brand_id', brandId);
    formData.append('creator_id', creatorId);
    formData.append('folder', folder);
    formData.append('tags', tags);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${BASE_URL}/api/drive/upload`);
      const token = getAuthToken();
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            resolve({});
          }
        } else {
          if (xhr.status === 401) handleUnauthorized();
          let msg = `Upload failed (${xhr.status})`;
          try {
            const err = JSON.parse(xhr.responseText);
            msg = err.detail || msg;
          } catch {}
          reject(new ApiError(msg, xhr.status));
        }
      };

      xhr.onerror = () => reject(new ApiError('Network error during upload'));
      xhr.onabort = () => reject(new ApiError('Upload cancelled'));
      if (onXhrCreated) onXhrCreated(xhr);
      xhr.send(formData);
    });
  },

  getFile: (id) => get(`/drive/files/${id}`),
  deleteFile: (id) => del(`/drive/files/${id}`),
  updateFile: (id, data) => patch(`/drive/files/${id}`, data),
  diskSpace: () => get('/drive/disk-space'),

  /**
   * Chunked upload — splits file into pieces to bypass proxy body-size limits.
   * Uses parallel uploads (6 concurrent) for maximum speed.
   * 5 MB chunks × 6 parallel = ~30 MB/round → ~3-5 min for a 5 GB video.
   */
  uploadFileChunked: async (file, { brandId, creatorId, folder = 'yt_raw', tags = '' }, onProgress, signal) => {
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB per chunk
    const PARALLEL = 6;                  // 6 concurrent uploads
    const fileId = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const token = localStorage.getItem('reelia_token');
    const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
    let completedChunks = 0;

    const uploadOneChunk = async (i) => {
      if (signal?.aborted) throw new ApiError('Upload cancelled', 0);
      const fd = new FormData();
      fd.append('file_id', fileId);
      fd.append('chunk_index', String(i));
      fd.append('total_chunks', String(totalChunks));
      fd.append('chunk', file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE), `chunk_${i}`);

      const res = await fetch(`${BASE_URL}/api/drive/upload-chunk`, {
        method: 'POST', headers: authHeader, body: fd, signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new ApiError(err.detail || `Fragmento ${i + 1}/${totalChunks} falló (${res.status})`, res.status);
      }
      completedChunks++;
      onProgress?.(Math.round((completedChunks / totalChunks) * 90));
    };

    // Upload chunks in parallel batches
    for (let i = 0; i < totalChunks; i += PARALLEL) {
      if (signal?.aborted) throw new ApiError('Upload cancelled', 0);
      await Promise.all(
        Array.from({ length: Math.min(PARALLEL, totalChunks - i) }, (_, k) => uploadOneChunk(i + k))
      );
    }

    // Finalize: assemble + thumbnail + DB record (no binary payload — no proxy issue)
    if (signal?.aborted) throw new ApiError('Upload cancelled', 0);
    onProgress?.(92);
    const fd = new FormData();
    fd.append('file_id', fileId);
    fd.append('filename', file.name);
    fd.append('brand_id', brandId);
    fd.append('creator_id', creatorId);
    fd.append('folder', folder);
    fd.append('tags', tags);
    fd.append('total_chunks', String(totalChunks));

    const res = await fetch(`${BASE_URL}/api/drive/finalize-upload`, {
      method: 'POST', headers: authHeader, body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new ApiError(err.detail || 'Finalización del upload falló', res.status);
    }
    onProgress?.(100);
    return res.json();
  },
};

// ─── DRIVE FOLDERS ──────────────────────────────────────────────────────────────
export const driveFoldersApi = {
  list: (brandId) => get(`/drive/folders?brand_id=${brandId}`),
  create: (data) => post('/drive/folders', data),
  rename: (id, name) => patch(`/drive/folders/${id}`, { name }),
  delete: (id) => del(`/drive/folders/${id}`),
};

// ─── PROCESSING ────────────────────────────────────────────────────────────────
export const processingApi = {
  createJob: (data) => post('/processing/jobs', data),
  createPipeline: (data) => post('/processing/pipeline', data),
  getJob: (id) => get(`/processing/jobs/${id}`),
  listJobs: (videoId) => get(`/processing/jobs${videoId ? `?video_id=${videoId}` : ''}`),
  detectMoments: (videoId, count = 5) =>
    post(`/processing/detect-moments/${videoId}?count=${count}`, {}),
  detectMomentsAsync: (videoId, count = 8) =>
    post(`/processing/detect-moments-async/${videoId}?count=${count}`, {}),
};

// ─── REELS ─────────────────────────────────────────────────────────────────────
export const reelsApi = {
  list: (brandId) => get(`/reels${brandId ? `?brand_id=${brandId}` : ''}`),
  create: (data) => post('/reels', data),
  get: (id) => get(`/reels/${id}`),
  update: (id, data) => patch(`/reels/${id}`, data),
  // La suscripción la resuelve el servidor; el cliente ya no la manda.
  generate: (id) => post(`/reels/${id}/generate`, {}),
  delete: (id) => del(`/reels/${id}`),
  scoreClips: (data) => post('/reels/score-clips', data),
  generateConcept: (data) => post('/reels/generate-concept', data),
};

// ─── MUSIC LIBRARY ──────────────────────────────────────────────────────────
export const musicApi = {
  list: (brandId) => get(`/music${brandId ? `?brand_id=${brandId}` : ''}`),
  upload: (formData) => request('POST', '/music/upload', formData, true),
  suggest: (data) => post('/music/suggest', data),
  delete: (id) => del(`/music/${id}`),
};

// ─── LIBRARY SEMANTIC INDEX ─────────────────────────────────────────────────
export const libraryApi = {
  search: (q, limit = 20, excludeSuggestive = false) =>
    get(`/library/search?q=${encodeURIComponent(q)}&limit=${limit}&exclude_suggestive=${excludeSuggestive}`),
  startIndex: () => post('/library/index', {}),
  indexStatus: () => get('/library/index/status'),
  remoderate: () => post('/library/remoderate', {}),
};

// ─── DRAFTS ─────────────────────────────────────────────────────────────────
export const draftsApi = {
  create: (templateId, brandId) => post('/drafts', { template_id: templateId, brand_id: brandId }),
  list: () => get('/drafts'),
  get: (id) => get(`/drafts/${id}`),
  patch: (id, data) => patch(`/drafts/${id}`, data),
  delete: (id) => del(`/drafts/${id}`),
  suggestions: (id, index, q = '', limit = 30) =>
    get(`/drafts/${id}/scene/${index}/suggestions?limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ''}`),
};

// ─── TEMPLATES ──────────────────────────────────────────────────────────────
export const templatesApi = {
  import: (formData) => request('POST', '/templates/import', formData, true),
  list: (niche) => get(`/templates${niche ? `?niche=${encodeURIComponent(niche)}` : ''}`),
  get: (id) => get(`/templates/${id}`),
  delete: (id) => del(`/templates/${id}`),
};

// ─── TRANSCRIPTION ──────────────────────────────────────────────────────────
export const transcribeApi = {
  clip: (videoId, { start, end, language } = {}) => {
    const params = new URLSearchParams();
    if (start !== undefined) params.set('start', start);
    if (end !== undefined) params.set('end', end);
    if (language && language !== 'auto') params.set('language', language);
    const q = params.toString();
    return post(`/transcribe/${videoId}${q ? `?${q}` : ''}`, {});
  },
};

// ─── REEL STUDIO ────────────────────────────────────────────────────────────
export const studioApi = {
  templates: () => get('/studio/templates'),
  categoryPreset: (type) => get(`/studio/category-preset/${type}`),
  analyzeVideo: (videoId) => post('/studio/analyze-video', { video_id: videoId }),
  uploadMusic: (formData) => request('POST', '/studio/upload-music', formData, true),
  analyzeMusic: (musicId, targetDuration = 30) => 
    post(`/studio/analyze-music/${musicId}?target_duration=${targetDuration}`, {}),
  createReel: (data) => post('/studio/create-reel', data),
  exportFinal: (jobId) => post(`/studio/export-final/${jobId}`, {}),
  getJob: (jobId) => get(`/studio/job/${jobId}`),
  listJobs: (brandId) => get(`/studio/jobs${brandId ? `?brand_id=${brandId}` : ''}`),
  deleteJob: (jobId) => request('DELETE', `/studio/job/${jobId}`),
};

// ─── VIRAL ENGINE ───────────────────────────────────────────────────────────────
export const viralApi = {
  generate: (data) => post('/viral/generate', data),
  get: (brandId) => get(`/viral/${brandId}`),
};

// ─── RISK ──────────────────────────────────────────────────────────────────────
export const riskApi = {
  scan: (data) => post('/risk/scan', data),
  listScans: (brandId) => get(`/risk/scans${brandId ? `?brand_id=${brandId}` : ''}`),
};

// ─── PUBLISHING ────────────────────────────────────────────────────────────────
export const publishApi = {
  listPosts: ({ brandId, status } = {}) => {
    const params = new URLSearchParams();
    if (brandId) params.set('brand_id', brandId);
    if (status) params.set('status', status);
    const q = params.toString();
    return get(`/posts${q ? `?${q}` : ''}`);
  },
  createPost: (data) => post('/posts', data),
  updatePost: (id, data) => patch(`/posts/${id}`, data),
  deletePost: (id) => del(`/posts/${id}`),
  publishNow: (id) => post(`/posts/${id}/publish`, {}),
};

// ─── A/B TESTS ─────────────────────────────────────────────────────────────────
export const abtestApi = {
  list: (brandId) => get(`/abtests${brandId ? `?brand_id=${brandId}` : ''}`),
  create: (data) => post('/abtests', data),
  update: (id, data) => patch(`/abtests/${id}`, data),
  pushWinner: (id) => post(`/abtests/${id}/push-winner`, {}),
};

// ─── BILLING ───────────────────────────────────────────────────────────────────
export const billingApi = {
  getBilling: (userId) => get(`/billing/${userId}`),
  subscribe: (data) => post('/billing/subscribe', data),
  cancel: (userId) => del(`/billing/cancel/${userId}`),
};

// ─── STATS ─────────────────────────────────────────────────────────────────────
export const statsApi = {
  get: ({ creatorId, brandId } = {}) => {
    const params = new URLSearchParams();
    if (creatorId) params.set('creator_id', creatorId);
    if (brandId) params.set('brand_id', brandId);
    const q = params.toString();
    return get(`/stats${q ? `?${q}` : ''}`);
  },
};

export { ApiError };
